/**
 * สรุปงานวันนี้ (Daily Briefing) — AI layer A6, the front-of-house surface.
 *
 * Shows open findings scoped to the caller's role (RLS-enforced server-side),
 * urgency-sorted, each with a deep-link to where the work gets done plus
 * mark-done / dismiss actions (dismiss needs a reason — the morning audit then
 * respects it). Works in rule-only mode; LLM summary shown when present.
 * Renders nothing when there's no work.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, AlertCircle, Info, ChevronRight, Sparkles, Check, X } from 'lucide-react';
import { useBriefing, useUpdateFinding, type BriefingFinding } from '../hooks';

const ACTION_ROUTE: Record<string, string> = {
  open_emergency_review: '/approvals#emergency',
  open_escalations: '/approvals',
  open_approvals: '/approvals',
  open_employees: '/hr/employees',
  run_reconcile: '/owner/cycle#reconcile',
  open_export: '/owner/settings',
  run_payroll: '/owner/cycle#payroll',
  open_payroll: '/owner/cycle#payroll',
  send_slips: '/owner/cycle#payroll',
  set_salary: '/hr/employees',
};

function routeFor(f: BriefingFinding): string | null {
  const kind = f.suggested_action?.kind;
  return (kind && ACTION_ROUTE[kind]) ?? null;
}

const SEVERITY_STYLE: Record<BriefingFinding['severity'], { icon: typeof Info; cls: string; ring: string }> = {
  ERROR: { icon: AlertCircle, cls: 'text-red-600', ring: 'border-l-red-500' },
  WARN: { icon: AlertTriangle, cls: 'text-amber-600', ring: 'border-l-amber-400' },
  INFO: { icon: Info, cls: 'text-gray-500', ring: 'border-l-gray-300' },
};

function FindingRow({ f }: { f: BriefingFinding }) {
  const { icon: Icon, cls, ring } = SEVERITY_STYLE[f.severity] ?? SEVERITY_STYLE.INFO;
  const to = routeFor(f);
  const update = useUpdateFinding();
  const [dismissing, setDismissing] = useState(false);
  const [reason, setReason] = useState('');
  const busy = update.isPending;

  return (
    <div className={`border-l-4 ${ring} bg-white rounded-r-lg`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <Icon className={`w-5 h-5 shrink-0 ${cls}`} />
        {to ? (
          <Link to={to} className="text-sm flex-1 hover:underline">{f.title}</Link>
        ) : (
          <span className="text-sm flex-1">{f.title}</span>
        )}
        {to && <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
        <button onClick={() => update.mutate({ id: f.id, status: 'done' })} disabled={busy}
          title="เสร็จแล้ว" className="p-1 text-gray-400 hover:text-brand-600">
          <Check className="w-4 h-4" />
        </button>
        <button onClick={() => setDismissing((v) => !v)} disabled={busy}
          title="ซ่อน" className="p-1 text-gray-400 hover:text-red-500">
          <X className="w-4 h-4" />
        </button>
      </div>

      {dismissing && (
        <div className="px-3 pb-2.5 flex gap-2">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="เหตุผลที่ซ่อน (จำเป็น)"
            className="input flex-1 text-xs py-1.5"
          />
          <button
            onClick={() => reason.trim() && update.mutate({ id: f.id, status: 'dismissed', reason: reason.trim() })}
            disabled={busy || !reason.trim()}
            className="btn-secondary text-xs px-3 py-1.5"
          >
            ยืนยัน
          </button>
        </div>
      )}
    </div>
  );
}

export function DailyBriefing() {
  const { data, isLoading } = useBriefing();

  if (isLoading) return <div className="card p-4 text-sm text-gray-500">กำลังตรวจงานค้าง…</div>;
  if (!data || data.findings.length === 0) return null;

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-brand-600" />
        <h2 className="font-display font-semibold">สรุปงานวันนี้</h2>
        <span className="ml-auto text-xs text-gray-500">{data.findings.length} เรื่อง</span>
      </div>

      {data.summary && (
        <p className="text-sm text-gray-700 bg-brand-50 rounded-lg p-3 leading-relaxed">{data.summary}</p>
      )}

      <div className="space-y-2">
        {data.findings.map((f) => (
          <FindingRow key={f.id} f={f} />
        ))}
      </div>
    </section>
  );
}
